import {
    Button as MUIButton,
    ButtonProps as MUIButtonProps,
    Link,
} from '@material-ui/core';
import {makeStyles, alpha} from '@material-ui/core/styles';
import {
    CONIFER_5,
    GRANITE_8,
    GRANITE_9,
    CONIFER_3,
    SNOW_BLIND,
} from '@gaia/colors';
import GaiaIcon, {GaiaIconProps} from '@gaia/icons/GaiaIcon';
import theme from '@gaia/theme';

// Most button styles are inherited from the global theme
const useStyles = makeStyles({
    root: {
        '&:focus': {
            outline: 'none',
        },
        '&.defaultFocused': {
            backgroundColor: alpha(
                CONIFER_3,
                theme.palette.action.hoverOpacity
            ),
        },
        '&.primaryFocused': {
            backgroundColor: CONIFER_5,
        },
        '&.secondaryFocused': {
            backgroundColor: GRANITE_8,
        },
        '&.destructFocused': {
            backgroundColor: GRANITE_8,
        },
        '&.outlinedFocused': {
            backgroundColor: GRANITE_9,
        },
    },
    link: {
        color: CONIFER_3,
    },
    outlineInverted: {
        color: SNOW_BLIND,
        borderColor: SNOW_BLIND,
        '&:hover': {
            backgroundColor: alpha(SNOW_BLIND, 0.25),
            borderColor: SNOW_BLIND,
            color: SNOW_BLIND,
        },
        '&.outlinedFocused': {
            backgroundColor: alpha(SNOW_BLIND, 0.25),
            color: SNOW_BLIND,
        },
    },
});

export interface ButtonProps
    extends Omit<
        MUIButtonProps,
        'disableRipple' | 'onFocusVisible' | 'buttonRef'
    > {
    /**
     * The visual appearance of the button. Overrides MUI `color` and `variant` props if provided.
     */
    appearance?:
        | 'default'
        | 'primary'
        | 'secondary'
        | 'destruct'
        | 'outlined'
        | 'link';
    /**
     * Invert the color of an outlined button.
     */
    inverted?: boolean;
    /**
     * Visually and functionally disables the button.
     */
    disabled?: boolean;
    /**
     * If `true`, the button will take up the full width of its container.
     */
    fullWidth?: boolean;
    /**
     * A URL. If provided, an `a` element will be rendered as the root node.
     */
    href?: React.AnchorHTMLAttributes<HTMLAnchorElement>['href'];
    /**
     * A custom icon to display at the left of the button. Uses naming conventions from `GaiaIcon`.
     */
    iconName?: GaiaIconProps['name'];
    /**
     * What happens when the button is clicked.
     */
    onClick?: (event: any) => void;
    /**
     * The size of the button. Defaults to `medium`.
     */
    size?: 'medium' | 'large' | 'small';
    /**
     * Optional styles.
     */
    style?: React.CSSProperties;
    /**
     * The content of the button.
     */
    children?: React.ReactNode;
    /**
     * If an href is provided, opens the URL in a new tab.
     */
    target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
    rel?: React.AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
}

// Map MUI style props to our button API
const mapMuiButtonProps: Record<string, MUIButtonProps> = {
    default: {
        variant: 'text',
        color: 'primary',
        focusVisibleClassName: 'defaultFocused',
    },
    primary: {
        variant: 'contained',
        color: 'primary',
        focusVisibleClassName: 'primaryFocused',
    },
    secondary: {
        variant: 'text',
        color: 'default',
        focusVisibleClassName: 'secondaryFocused',
    },
    destruct: {
        variant: 'text',
        color: 'secondary',
        focusVisibleClassName: 'destructFocused',
    },
    outlined: {
        variant: 'outlined',
        color: 'primary',
        focusVisibleClassName: 'outlinedFocused',
    },
};

/**
 * `<Button />` wraps the MUI Button component and inherits its props.
 */
export default function Button({
    appearance = 'default',
    disabled = false,
    size,
    iconName,
    inverted,
    onClick,
    ...props
}: ButtonProps) {
    const classes = useStyles();
    const {style, children, ...buttonProps} = props;
    const fontSize = size === 'small' ? 12 : size === 'large' ? 18 : 14;
    const buttonStyles = {
        root: classes.root,
        ...(inverted && {outlinedPrimary: classes.outlineInverted}),
    };
    return appearance === 'link' ? (
        <Link
            classes={{root: classes.link}}
            component='button'
            disabled={disabled}
            style={{
                ...style,
                fontSize,
            }}
            onClick={onClick}
        >
            {children}
        </Link>
    ) : (
        <MUIButton
            {...buttonProps}
            {...mapMuiButtonProps[appearance]}
            disabled={disabled}
            onClick={onClick}
            classes={buttonStyles}
            size={size}
            style={style}
        >
            {iconName && (
                <GaiaIcon
                    name={iconName}
                    style={{marginRight: 8, marginLeft: -4}}
                />
            )}
            {children}
        </MUIButton>
    );
}
